import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { http } from '@/api/http';
import { useAuthStore } from '@/auth/store';
import { authApi } from '@/auth/api';
import {
  useTodayCards,
  useRegularity,
  useModulesSummary,
  useRecentSessions,
} from '@/api/dashboard';
import { useRatings } from '@/api/ratings';
import { buildNavChips } from './navChips';
import { useAwardXp, useXpDisplay } from '@/api/xp';
import { FixedNav } from '@/ui/FixedNav';
import { Card, Caption, Button, Badge, StatStrip } from '@/ui/primitives';
import { Heatmap, ProgressRing, PulseDot, AnimatedNumber, Sparkline } from '@/ui/telemetry';
import { KPI, ModuleTile } from '@/ui/KPI';
import { SessionDetailModal } from '@/ui/modals';
import type { SessionDetail } from '@/ui/modals';
import { OnboardingModal } from '@/ui/OnboardingModal';
import { MODULE_SLIDES } from '@/ui/onboardingSlides';
import { XpExplainer } from '@/ui/XpExplainer';
import { ErrorMessageWithDebug } from '@/ui/ErrorMessageWithDebug';
import { BriefTask } from './BriefTask';
import {
  buildTasksOfTheDay,
  buildTasksFromAdaptiveBrief,
  formatStreakDays,
  formatRecallOpeningPct,
  formatBlunderRate,
} from './briefTasks';
import type { BriefTaskDescriptor } from './briefTasks';

/**
 * Mission Control — cockpit fusion V2 (PRODUCT.md §4.1).
 *
 * Réécriture complète du placeholder MVP1.2. Consomme les hooks fusion +
 * la source unifiée des ratings (T4 P1 Stats Hub) :
 *   useTodayCards, useRegularity(90), useModulesSummary(30),
 *   useRecentSessions(5), useRatings()
 *
 * XP du proto conservé (cf. docs/XP_BAREME.md) : 4 tasks BriefTask avec XP
 * fixes 25/30/25/20 (total 100/jour). Distinction clarifiée 2026-05-16 dans
 * PRODUCT §5.4 : la règle anti-streaks ne concerne pas les points d'XP par
 * exercice — XP est un feedback positif neutre, pas une mécanique addictive.
 */

interface HealthResponse {
  status: 'ok' | 'degraded';
  version: string;
  timestamp: string;
  db: string;
}

export function MissionControl(): JSX.Element {
  // useAuthStore.user n'est plus lu directement (T4 P1 : tous les ratings
  // passent par useRatings()). On garde `clear` pour le logout.
  const clear = useAuthStore((s) => s.clear);
  const navigate = useNavigate();
  const [selectedSession, setSelectedSession] = useState<SessionDetail | null>(null);

  const health = useQuery<HealthResponse>({
    queryKey: ['health'],
    queryFn: () => http.get<HealthResponse>('/api/v1/health'),
    retry: 0,
  });
  const today = useTodayCards();
  const regularity = useRegularity(90);
  const modules = useModulesSummary(30);
  const sessions = useRecentSessions(5);
  // T4 P1 Stats Hub : source unifiée. Remplace useChesscomRatings() +
  // lectures Zustand directes user?.lichess_ratings?.* (cf. RATINGS_AUDIT §2.1/2.4/2.9).
  const ratings = useRatings();

  // T5 MVP2.5 : XP persistence + display footer. Idempotence backend 1h sur
  // (user, module, source_ref) → cliquer 2x une même BriefTask le même jour
  // n'award qu'1x. todayDate = YYYY-MM-DD local pour avoir un source_ref
  // stable sur la journée.
  const awardXp = useAwardXp();
  const xpDisplay = useXpDisplay();
  const todayDate = new Date().toISOString().slice(0, 10);
  // Barème XP mission_brief : +25 par task complétée (cf. docs/XP_BAREME.md).
  const XP_PER_MISSION_BRIEF = 25;

  // T4 P1 Stats Hub : lectures via snapshot unifié. Fallback "—" si absent
  // (suppression des literals hardcodés `?? 2144` cf. RATINGS_AUDIT §2.3).
  const snapshot = ratings.data;
  const fideRapide = snapshot?.fide?.rapide?.value ?? null;
  const lichessPuzzle = snapshot?.lichess?.puzzle?.value ?? null;

  async function handleLogout(): Promise<void> {
    try {
      await authApi.logout();
    } catch (e) {
      console.error('logout failed', e);
    }
    clear();
    window.location.assign('/login');
  }

  // Heatmap 90j : remplit les jours sans tentative à 0, scale 0..4.
  const heatmapData = buildHeatmapDays(regularity.data?.rows ?? [], 91);

  const m = modules.data?.modules;
  const moduleData = [
    { id: 'opening' as const, label: 'Opening Lab', tagline: 'Armes sales, exécution propre.', color: 'var(--violet-500)', path: '/opening-lab', stats: m?.opening },
    { id: 'tactic' as const, label: 'Tactics Dojo', tagline: 'Vois le coup avant la panique.', color: 'var(--cyan-500)', path: '/tactics-dojo', stats: m?.tactic },
    { id: 'autopsy' as const, label: 'Game Autopsy', tagline: 'Ce n’est pas une défaite. C’est un dataset.', color: 'var(--coral-500)', path: '/game-autopsy', stats: m?.autopsy },
    { id: 'endgame' as const, label: 'Endgame Forge', tagline: 'Là où les avantages deviennent des victoires.', color: 'var(--amber-500)', path: '/endgame-forge', stats: m?.endgame },
  ];

  const totalAttempts30d = m
    ? m.opening.attempts + m.tactic.attempts + m.endgame.attempts + m.autopsy.attempts
    : 0;
  const totalSuccess30d = m
    ? m.opening.success + m.tactic.success + m.endgame.success + m.autopsy.success
    : 0;
  const overallRate = totalAttempts30d > 0 ? Math.round((totalSuccess30d / totalAttempts30d) * 100) : 0;

  const crossOriginOk = typeof self !== 'undefined' && self.crossOriginIsolated === true;
  const navChips = buildNavChips(snapshot);

  // ─── Tasks du jour avec XP (barème docs/XP_BAREME.md) ───────────────────
  // T5 MVP2.5 : clic d'une BriefTask = award mission_brief 25 XP backend +
  // navigation. source_ref `brief_<date>_<key>` + idempotence 1h évite les
  // doubles awards en cas de double-tap tablette.
  //
  // T6 MVP2.5 : la liste tasks est construite par `buildTasksOfTheDay()` à
  // partir des compteurs `<module>_target_today` / `_done_today` renvoyés
  // par `/training/today`. Tasks invisibles si target_today === 0,
  // `done = done_today >= target_today`, compteur "done / target" affiché.
  function awardBriefAndNavigate(taskKey: string, target: string): void {
    awardXp.mutate({
      module: 'mission_brief',
      source_ref: `brief_${todayDate}_${taskKey}`,
      xp_amount: XP_PER_MISSION_BRIEF,
    });
    navigate(target);
  }

  // MVP2.6 Gap 4 (P2.1) — Mission Control adaptatif V1.
  // Si le backend renvoie `adaptive_brief` (4 BriefTask priorisées), on
  // l'utilise EN PREMIER. Sinon fallback sur les compteurs statiques.
  const adaptiveTasks = buildTasksFromAdaptiveBrief(
    today.data?.adaptive_brief,
    awardBriefAndNavigate,
  );
  const staticTasks: BriefTaskDescriptor[] = buildTasksOfTheDay(
    today.data,
    awardBriefAndNavigate,
  );
  const tasks: BriefTaskDescriptor[] = adaptiveTasks.length > 0
    ? adaptiveTasks
    : staticTasks;
  const totalXp = tasks.reduce((sum, t) => sum + t.xp, 0);
  const earnedXp = tasks.reduce((sum, t) => sum + (t.done ? t.xp : 0), 0);
  // Index de la tâche "current" = première non-done. -1 si tout est validé.
  // Sert à activer le scan-line acid (mission-step-active) sur cette seule step.
  const activeTaskIndex = tasks.findIndex((t) => !t.done);

  return (
    <>
      {/* T7 MVP2.5 — Onboarding 1ère visite Mission Control (bug A8). */}
      <OnboardingModal
        moduleSlug="mission-control"
        moduleLabel="Mission Control"
        tone="acid"
        slides={MODULE_SLIDES['mission-control'] ?? []}
      />
      <FixedNav chips={navChips} />
      <main className="mx-auto max-w-[1280px] px-6 py-8 flex flex-col gap-6">
        {/* Header — caption + h1 + dépose tag */}
        <header className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <Caption color="paper">Mission Control</Caption>
            <h1 className="font-display text-display-2 text-paper-50 mt-1">
              Salut Léon<span className="text-ink-300">.</span>
            </h1>
            <p className="text-body text-ink-300 italic mt-0.5">Ta zone de déploiement.</p>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="text-body-sm text-ink-300 underline-offset-4 hover:text-coral-500 hover:underline"
          >
            Déconnexion
          </button>
        </header>

        {/* Mission briefing — progress ring (XP) + 4 tasks avec XP.
            mc-brief = radial-gradient acid+violet d'ambiance cockpit ; scanwrap =
            balayage acid 5s (cf. globals.css). */}
        <Card padding="default" className="mc-brief scanwrap">
          <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6 items-center">
            <div className="flex flex-col items-center gap-2">
              <ProgressRing
                value={totalXp > 0 ? Math.round((earnedXp / totalXp) * 100) : 0}
                size={120}
                thickness={8}
                color="var(--acid-500)"
                label={`${earnedXp}`}
              />
              <Caption>{earnedXp} / {totalXp} pts</Caption>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <h2 className="font-display text-heading-2 text-paper-50">Aujourd'hui — 45 min d'exercice</h2>
                <span className="font-mono text-xs text-ink-300">
                  <span className="text-acid-500 font-medium">{earnedXp}</span> / {totalXp} pts gagnés
                </span>
              </div>
              <ul className="flex flex-col gap-2 mt-2">
                {tasks.length === 0 && (
                  <li className="text-ink-300 italic text-sm">
                    Rien à faire aujourd'hui — repos mérité.
                  </li>
                )}
                {tasks.map((t, i) => (
                  <BriefTask
                    key={t.key}
                    done={t.done}
                    active={!t.done && i === activeTaskIndex}
                    label={t.label}
                    counter={t.counter}
                    accent={t.accent}
                    xp={t.xp}
                    onClick={t.onClick}
                  />
                ))}
              </ul>
              {/* T5 MVP2.5 — footer XP persistance discret (Caption ink-300,
                  pas de gamification théâtrale, cf. PRODUCT §5.4). */}
              <p className="mt-3 text-caption text-ink-300 font-mono tabular-nums">
                XP aujourd'hui&nbsp;: <span className="text-paper-50">{xpDisplay.today}</span>
                {' · '}Streak&nbsp;: <span className="text-paper-50">{xpDisplay.streak}</span>{' '}
                jour{xpDisplay.streak === 1 ? '' : 's'}
                {' · '}Total all-time&nbsp;: <span className="text-paper-50">{xpDisplay.alltime}</span>
              </p>
              {/* T7 MVP2.5 — XpExplainer collapsible (bug A8) : Léon comprend
                  le barème complet sans encombrer le cockpit. Fermé par défaut. */}
              <XpExplainer className="mt-3" />
            </div>
          </div>
        </Card>

        {/* 4 KPIs avec stagger 80ms par tile (effet "tableau qui s'allume").
            T4 P1 Stats Hub : FIDE Rapide + Lichess Puzzle lus depuis useRatings()
            au lieu de literals hardcodés. KPI accepte value={number} pour
            l'AnimatedNumber — on garde 0 quand la donnée manque ET on
            affiche un fallback texte via children (cf. KPI.tsx line 80). */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPI
            label="FIDE Rapide"
            value={fideRapide ?? 0}
            target="2000 · M6"
            color="var(--acid-500)"
            delay={0}
          >
            {fideRapide == null && (
              <button
                type="button"
                onClick={() => navigate('/stats?tab=fide')}
                className="mt-2 text-[11px] font-mono text-acid-500 underline-offset-4 hover:underline self-start"
              >
                Saisis ton rating FIDE →
              </button>
            )}
          </KPI>
          <KPI
            label="Lichess Puzzle"
            value={lichessPuzzle ?? 0}
            target="2300"
            color="var(--cyan-500)"
            delay={80}
          />
          <KPI
            label="Taux de réussite 30j"
            value={overallRate}
            format={(v) => `${Math.round(v)}%`}
            color="var(--volt-500)"
            target="65%"
            delay={160}
          />
          <KPI
            label="Tentatives 30j"
            value={totalAttempts30d}
            color="var(--violet-500)"
            target="500"
            delay={240}
          />
        </section>

        {/* MVP2.6 Gap 3 (P2.6) — KPIs Mission Control élargis (PRODUCT.md §4.0).
            Série de jours · Recall ouverture · Blunder rate 7j. Wording strict
            "froidement motivant" (DESIGN.md §6) : pas d'emoji, pas de "🔥",
            valeur nulle = "—" (jamais "0"). */}
        <section
          className="grid grid-cols-1 sm:grid-cols-3 gap-4"
          aria-label="Indicateurs pédagogiques étendus"
        >
          <KPI
            label="Série"
            value={today.data?.streak_days ?? 0}
            format={() => formatStreakDays(today.data?.streak_days)}
            color="var(--volt-500)"
            target="30 j."
            delay={320}
          />
          <KPI
            label="Recall ouverture"
            value={today.data?.recall_opening_pct ?? 0}
            format={() => formatRecallOpeningPct(today.data?.recall_opening_pct)}
            color="var(--violet-500)"
            target="80%"
            delay={400}
          />
          <KPI
            label="Blunder rate 7 j."
            value={today.data?.blunder_rate_7d ?? 0}
            format={() => formatBlunderRate(today.data?.blunder_rate_7d)}
            color="var(--coral-500)"
            target="< 20%"
            delay={480}
          />
        </section>

        {/* 4 module tiles (stagger continue à partir du dernier KPI) */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {moduleData.map((mod, i) => (
            <ModuleTile
              key={mod.id}
              label={mod.label}
              tagline={mod.tagline}
              color={mod.color}
              progress={mod.stats?.mastery_pct ?? 0}
              footer={`${mod.stats?.attempts ?? 0} tentatives · 30j`}
              onClick={() => navigate(mod.path)}
              delay={320 + i * 80}
            />
          ))}
        </section>

        {/* Activity heatmap + sessions */}
        <section className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4">
          <Card delay={640}>
            <div className="flex items-center justify-between mb-3">
              <Caption>Activité · 90 jours</Caption>
              <span className="font-mono text-[11px] text-ink-300 tabular-nums">
                {regularity.data?.days_active ?? 0} jours actifs
              </span>
            </div>
            <Heatmap data={heatmapData} cell={11} gap={3} weeks={13} color="var(--volt-500)" />
            <div className="flex items-center gap-3 mt-3 text-[10px] text-ink-300 font-mono uppercase tracking-wider">
              <span>moins</span>
              {[0, 1, 2, 3, 4].map((level) => (
                <span
                  key={level}
                  className="w-2.5 h-2.5 rounded-sm"
                  style={{
                    background: level === 0 ? 'var(--ink-700)' : 'var(--volt-500)',
                    opacity: level === 0 ? 1 : 0.08 + (level / 4) * 0.92,
                  }}
                />
              ))}
              <span>plus</span>
            </div>
          </Card>

          <Card delay={720}>
            <Caption>Sessions récentes</Caption>
            <ul className="flex flex-col gap-2 mt-3">
              {(sessions.data?.sessions ?? []).slice(0, 5).map((s) => (
                <li key={s.id}>
                  <button
                    onClick={() => setSelectedSession(s)}
                    className="w-full text-left bg-ink-900 hover:bg-ink-700/40 border border-ink-700 rounded-lg px-3 py-2 flex items-center justify-between transition-all duration-150"
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="text-paper-50 text-sm">{s.date}</span>
                      <span className="text-ink-300 text-xs font-mono">{s.start_time} · {s.duration_min} min</span>
                    </div>
                    <div className="flex flex-col items-end gap-0.5">
                      <span className="text-acid-500 font-mono text-xs tabular-nums">{s.attempts} tentatives</span>
                      <span className="text-ink-300 font-mono text-[10px] tabular-nums">{s.attempts > 0 ? Math.round((s.success / s.attempts) * 100) : 0}% réussite</span>
                    </div>
                  </button>
                </li>
              ))}
              {(sessions.data?.sessions ?? []).length === 0 && (
                <li className="text-ink-300 italic text-sm">Aucune session récente.</li>
              )}
            </ul>
          </Card>
        </section>

        {/* Sources chess.com + Parent CTA */}
        <ChesscomSection />

        <section className="flex items-center justify-between flex-wrap gap-3">
          <Button variant="ghost" onClick={() => navigate('/parent')}>
            Espace parent →
          </Button>
          <StatStrip className="flex-1">
            <StatChip
              label="API"
              value={health.isSuccess ? `OK ${health.data?.version ?? ''}` : 'KO'}
              tone={health.isSuccess ? 'volt' : 'coral'}
            />
            <StatChip
              label="DB"
              value={health.data?.db ?? '…'}
              tone={health.data?.db === 'ok' ? 'volt' : 'coral'}
            />
            <StatChip
              label="Stockfish multi-thread"
              value={crossOriginOk ? 'actif' : 'inactif'}
              tone={crossOriginOk ? 'volt' : 'coral'}
            />
          </StatStrip>
        </section>
      </main>

      <SessionDetailModal session={selectedSession} onClose={() => setSelectedSession(null)} />
    </>
  );
}

// ─── Composants internes ───────────────────────────────────────────────────

function StatChip({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'volt' | 'coral' | 'amber';
}): JSX.Element {
  const color = {
    volt: 'text-volt-500',
    coral: 'text-coral-500',
    amber: 'text-amber-500',
  }[tone];
  return (
    <span className="flex items-center gap-1.5 font-mono text-[11px]">
      <span className="text-ink-300 uppercase tracking-wider">{label}</span>
      <span className={color}>{value}</span>
    </span>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function buildHeatmapDays(rows: Array<{ day: string; attempts: number }>, totalDays: number): number[] {
  const map = new Map(rows.map((r) => [r.day, r.attempts]));
  const today = new Date();
  const out: number[] = [];
  for (let i = totalDays - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const count = map.get(key) ?? 0;
    // 0..4 scale : 0=0, 1=1-3, 2=4-8, 3=9-15, 4=16+
    let level = 0;
    if (count >= 16) level = 4;
    else if (count >= 9) level = 3;
    else if (count >= 4) level = 2;
    else if (count >= 1) level = 1;
    out.push(level);
  }
  return out;
}

// buildNavChips extrait dans `./navChips.ts` (T7 P1 Stats Hub) pour
// permettre les tests unitaires + respecter react-refresh/only-export-components.

// ─── Chesscom Section (préservée du MVP1.2 — refactor cosmétique uniquement) ──

type FeedbackKind =
  | { kind: 'success'; message: string }
  | { kind: 'error'; message: string; debug?: string };

function ChesscomSection(): JSX.Element {
  const [username, setUsername] = useState('');
  const [feedback, setFeedback] = useState<FeedbackKind | null>(null);

  const setMutation = useMutation({
    mutationFn: (u: string) =>
      http.post<{ chesscom_username: string }>('/api/v1/users/me/chesscom-username', {
        chesscom_username: u,
      }),
    onSuccess: (data) => {
      setFeedback({
        kind: 'success',
        message: `Pseudo chess.com enregistré : ${data.chesscom_username}`,
      });
    },
    onError: (err) => {
      const { message, debug } = formatApiError(err);
      setFeedback({ kind: 'error', message: `Sauvegarde échouée — ${message}`, ...(debug ? { debug } : {}) });
    },
  });

  const importMutation = useMutation({
    mutationFn: () =>
      http.post<{ imported_count: number; skipped_count: number; months_checked: number }>(
        '/api/v1/games/import-chesscom',
        {},
      ),
    onSuccess: (data) => {
      setFeedback({
        kind: 'success',
        message: `Import : ${data.imported_count} nouvelles, ${data.skipped_count} déjà connues, ${data.months_checked} mois scannés.`,
      });
    },
    onError: (err) => {
      const { message, debug } = formatApiError(err);
      setFeedback({ kind: 'error', message: `Import échoué — ${message}`, ...(debug ? { debug } : {}) });
    },
  });

  return (
    <Card delay={800}>
      <Caption>Source chess.com</Caption>
      <p className="mt-2 text-body-sm text-ink-300">
        Renseigne ton pseudo chess.com pour importer aussi tes parties Rapid (cron quotidien automatique).
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="ton pseudo chess.com"
          className="rounded-lg border border-ink-700 bg-ink-900 px-3 py-2 font-mono text-body-sm text-paper-50 focus:border-acid-500 focus:outline-none h-10"
        />
        <Button
          variant="primary"
          size="default"
          onClick={() => username !== '' && setMutation.mutate(username)}
          disabled={setMutation.isPending || username === ''}
        >
          Enregistrer
        </Button>
        <Button
          variant="secondary"
          size="default"
          onClick={() => importMutation.mutate()}
          disabled={importMutation.isPending}
        >
          {importMutation.isPending ? 'Import en cours…' : 'Importer maintenant'}
        </Button>
      </div>
      {feedback?.kind === 'success' && (
        <div className="mt-3">
          <Badge tone="muted">{feedback.message}</Badge>
        </div>
      )}
      {feedback?.kind === 'error' && (
        <div className="mt-3">
          <ErrorMessageWithDebug
            className="text-body-sm text-coral-500"
            message={feedback.message}
            debug={feedback.debug}
          />
        </div>
      )}
    </Card>
  );
}

/**
 * Pattern "kid-friendly + debug inline" appliqué aux erreurs API du module
 * chess.com (MissionControl). Renvoie un message Léon-friendly + un debug
 * technique optionnel destiné à `<ErrorMessageWithDebug>`.
 */
function formatApiError(err: unknown): { message: string; debug?: string } {
  if (typeof err === 'object' && err !== null) {
    const e = err as { status?: number; body?: { error?: string; detail?: string } };
    const status = e.status ?? '?';
    const code = e.body?.error ?? '';
    const detail = e.body?.detail ?? '';
    const debugParts = [`HTTP ${status}`, code, detail].filter((s) => s !== '');
    const debug = debugParts.join(' — ');
    return debug !== ''
      ? { message: 'Le serveur a refusé la requête.', debug }
      : { message: 'Erreur inconnue côté serveur.' };
  }
  if (err instanceof Error) {
    return { message: 'Échec de la requête.', debug: err.message };
  }
  return { message: 'Erreur inconnue.' };
}

// Pour le typecheck strict on satisfait l'import circulaire éventuel
void AnimatedNumber;
void Sparkline;
void PulseDot;
